import { createSlice } from '@reduxjs/toolkit';
import { get3rdPartyResponse, listSyncLogs } from '../../api/gatewise_api';
import { generateExcel } from './utils';
import { notifySuccess } from '../notifications/notificationsSlice';

const syncLogSlice = createSlice({
  name: 'syncLog',
  initialState: {
    page: {
      items: [],
      total: 0,
    },
  },
  reducers: {
    setPage: (state, action) => {
      const { page, error } = action.payload;
      state.page = page;
      state.error = error;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setErrorNotifications: (state, action) => {
      state.notifications = {
        error: action.payload,
      };
    },
  },
});

export const { setPage, setLoading, setErrorNotifications } = syncLogSlice.actions;

export default syncLogSlice.reducer;

export const loadPage = (communityId, offset, limit) => async (dispatch) => {
  if (!communityId) return;

  try {
    dispatch(setLoading(true));
    const { items, total } = await listSyncLogs(communityId, offset, limit);
    dispatch(setPage({ page: { items, total }, error: null }));
  } catch (error) {
    dispatch(
      setPage({
        page: {
          items: [],
          total: 0,
        },
        error: error.toString(),
      }),
    );
  } finally {
    dispatch(setLoading(false));
  }
};

export const exportToExcel = (community) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    const { items } = await listSyncLogs(community.id, 0, 99999);
    generateExcel(items, community);
    dispatch(notifySuccess('Export completed'));
  } catch (err) {
    dispatch(setErrorNotifications(err.toString()));
  } finally {
    dispatch(setLoading(false));
  }
};

export const downloadLastResponse = (community) => async (dispatch) => {
  try {
    const urls = await get3rdPartyResponse(community.id, community.integration.type);

    if (!Array.isArray(urls)) {
      dispatch(setErrorNotifications('Invalid response: expected array of URLs'));
      return;
    }

    if (urls.length === 0) {
      dispatch(setErrorNotifications('No files available for download'));
      return;
    }

    const invalidUrls = urls.map((url, index) => ({ url, index })).filter(({ url }) => !isValidUrl(url));
    if (invalidUrls.length > 0) {
      const indices = invalidUrls.map(({ index }) => index).join(', ');
      dispatch(setErrorNotifications(`Invalid URLs at indices: ${indices}`));
      return;
    }

    // Single file: Direct browser navigation triggers download
    if (urls.length === 1) {
      window.location.href = urls[0];
      dispatch(notifySuccess('Downloading file...'));
      return;
    }

    urls.forEach((url, index) => {
      setTimeout(() => {
        // Extract integration type from URL for better UX
        const urlMatch = url.match(/community_3rd_party_response\/\d+_\d+_(\w+)\./);
        const integrationType = urlMatch ? urlMatch[1].toUpperCase() : `File ${index + 1}`;

        // Notify about current file download
        dispatch(notifySuccess(`Downloading ${integrationType} response (${index + 1}/${urls.length})`));

        const link = document.createElement('a');
        link.href = url;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        // Clean up immediately after click
        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);
      }, index * 500); // 500ms delay between downloads to avoid browser blocking
    });

    // Final notification when all downloads are initiated
    setTimeout(() => {
      dispatch(notifySuccess('All downloads initiated. Check your downloads folder.'));
    }, urls.length * 500 + 500);
  } catch (err) {
    dispatch(setErrorNotifications(err.toString()));
  }
};

// URL validation function
function isValidUrl(url) {
  try {
    const parsedUrl = new URL(url);

    // Allow only HTTPS protocols
    return parsedUrl.protocol === 'https:';
  } catch (e) {
    return false;
  }
}
